import { useCallback, useEffect, useState } from 'react';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../context/ToastContext';
import { toGeneratedImageUrl } from '../../lib/images';
import {
  useLoadDraft,
  useRemoveDestinations,
  useSubmitDraft,
} from '../../hooks/useModerateDestinations';

const TYPES = [
  { key: 'popular', label: 'Popular' },
  { key: 'trending', label: 'Trending' },
];

const titleCase = (s = '') =>
  s.replace(/\w\S*/g, (w) => w[0].toUpperCase() + w.slice(1).toLowerCase());

const cityKey = (c) => (c.city || '').toLowerCase();

export default function DestinationModerationPage() {
  const toast = useToast();
  const [type, setType] = useState('popular');
  const [destinations, setDestinations] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [added, setAdded] = useState(() => new Set()); // cities newly filled in by last remove
  const [dirty, setDirty] = useState(false);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const loadM = useLoadDraft();
  const removeM = useRemoveDestinations();
  const submitM = useSubmitDraft();

  const applyDraft = useCallback((res, addedSet = new Set()) => {
    const list = res?.data?.destinations || [];
    setDestinations(list);
    setAdded(addedSet);
    setSelected(new Set());
    setError(null);
  }, []);

  // (Re)load the draft whenever the active type changes.
  useEffect(() => {
    let alive = true;
    setError(null);
    setDirty(false);
    setSelected(new Set());
    setAdded(new Set());
    loadM
      .mutate(type)
      .then((res) => {
        if (alive) applyDraft(res);
      })
      .catch((e) => {
        if (alive) {
          setDestinations([]);
          setError(e.message || 'Failed to load destinations.');
        }
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const toggle = (city) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(city) ? next.delete(city) : next.add(city);
      return next;
    });

  const remove = (cities) => {
    if (!cities.length) return;
    const before = new Set(destinations.map(cityKey));
    removeM
      .mutate({ type, cities })
      .then((res) => {
        // Highlight cities in the refreshed draft that weren't there before.
        const next = res?.data?.destinations || [];
        const addedSet = new Set(next.map(cityKey).filter((c) => !before.has(c)));
        applyDraft(res, addedSet);
        setDirty(true);
        toast.success(
          cities.length > 1
            ? `Removed ${cities.length} · replacements filled in`
            : 'Removed · replacement filled in'
        );
      })
      .catch((e) => {
        // 404 = none of the cities were in the draft; 400 = invalid type. Show inline.
        setError(e.message || 'Removal failed.');
        toast.error(e.message || 'Removal failed');
      });
  };

  const removeSelected = () => remove(Array.from(selected));

  const publish = () => {
    submitM
      .mutate(type)
      .then(() => {
        setConfirmOpen(false);
        setDirty(false);
        toast.success(`${titleCase(type)} destinations published live`);
      })
      .catch((e) => toast.error(e.message || 'Publish failed'));
  };

  const isLoading = loadM.isPending;
  const busy = removeM.isPending;

  return (
    <div className="page">
      <div className="page__header">
        <h1>Destination Moderation</h1>
        <button
          className="btn btn--primary"
          disabled={submitM.isPending || isLoading || destinations.length === 0}
          onClick={() => setConfirmOpen(true)}
        >
          Submit / Publish
        </button>
      </div>

      <div className="page__toolbar page__toolbar--row">
        <div className="seg">
          {TYPES.map((t) => (
            <button
              key={t.key}
              type="button"
              className={`seg__btn${type === t.key ? ' seg__btn--active' : ''}`}
              onClick={() => setType(t.key)}
              disabled={isLoading || busy}
            >
              {t.label}
            </button>
          ))}
        </div>
        {dirty && <span className="badge badge--itinerary">Unpublished changes</span>}
      </div>

      {destinations.length > 0 && (
        <div className="bulk-bar">
          <span className="bulk-bar__count" style={{ marginLeft: 0 }}>
            {destinations.length} destinations · {selected.size} selected
          </span>
          <button
            className="btn btn--sm btn--danger"
            style={{ marginLeft: 'auto' }}
            disabled={selected.size === 0 || busy}
            onClick={removeSelected}
          >
            {busy ? 'Removing…' : 'Remove selected'}
          </button>
          {selected.size > 0 && (
            <button className="btn btn--sm btn--ghost" onClick={() => setSelected(new Set())}>
              Clear
            </button>
          )}
        </div>
      )}

      {isLoading ? (
        <div className="state state--loading">Loading…</div>
      ) : error ? (
        <div className="state state--error" role="alert">
          {error}
        </div>
      ) : destinations.length === 0 ? (
        <div className="state state--empty">No destinations in this draft.</div>
      ) : (
        <div className="img-grid">
          {destinations.map((d) => {
            const key = cityKey(d);
            const checked = selected.has(key);
            const isNew = added.has(key);
            const imgSrc = toGeneratedImageUrl(d.image);
            return (
              <div
                className={`img-card dest-card${checked ? ' img-card--selected' : ''}${
                  isNew ? ' dest-card--new' : ''
                }`}
                key={key || d.image}
              >
                <label className="img-card__check">
                  <input type="checkbox" checked={checked} onChange={() => toggle(key)} />
                </label>
                {isNew && <span className="img-card__flag img-card__flag--on">New</span>}
                <div
                  className="img-card__thumb"
                  role="button"
                  tabIndex={0}
                  aria-pressed={checked}
                  onClick={() => toggle(key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggle(key);
                    }
                  }}
                >
                  {imgSrc ? (
                    <img src={imgSrc} alt={titleCase(d.city)} loading="lazy" />
                  ) : (
                    <div className="img-card__missing">no image</div>
                  )}
                </div>
                <div className="img-card__meta">
                  <span className="dest-card__city">{titleCase(d.city)}</span>
                  {d.state && <span className="dest-card__state">{d.state}</span>}
                  {d.avg_rating != null && (
                    <span className="dest-card__rating">★ {d.avg_rating}</span>
                  )}
                </div>
                <button
                  className="btn btn--sm btn--danger dest-card__remove"
                  disabled={busy}
                  onClick={() => remove([key])}
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      )}

      <ConfirmDialog
        open={confirmOpen}
        title="Publish destinations?"
        message="This will make the current set live for all users."
        confirmLabel="Publish"
        pending={submitM.isPending}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={publish}
      />
    </div>
  );
}
